import { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Home } from 'lucide-react';
import { apiFetch } from '@/lib/api';
import { useRootLabel } from '@/stores/scope';
import { formatCurrency, formatPercent, formatCount } from '@/lib/format';

// ============================================================================
// 型定義（Backend の /pl-tree エンドポイントに対応）
// ============================================================================
type MenuNode = {
  menu_id: string;
  menu_code: string;
  menu_name: string;
  effective_from: string;
  effective_to: string | null;
  unit_price: number;
  qty: number;
  amount: number;
};
type RevenueCategory = {
  menu_category_id: string | null;
  category_name: string;
  total: number;
  menus: MenuNode[];
};
type CogsItem = { ingredient_name: string; amount: number; ratio: number | null };
type CogsCategory = {
  cost_category_id: string | null;
  category_name: string;
  total: number;
  ratio: number | null;
  items: CogsItem[];
};
type OpexItem = { item_name: string; amount: number; ratio: number | null };
type PlTreeResponse = {
  period: { start: string; end: string; label: string };
  revenue: { total: number; categories: RevenueCategory[] };
  cost: {
    total: number;
    cogs: { total: number; ratio: number | null; categories: CogsCategory[] };
    labor: { total: number; ratio: number | null };
    opex: { total: number; ratio: number | null; items: OpexItem[] };
  };
  operating_profit: number;
};

type Store = { store_id: string; store_name: string };

/** 対象月候補（直近24ヶ月） */
const buildMonthOptions = (): string[] => {
  const opts: string[] = [];
  const now = new Date();
  for (let i = 0; i < 24; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`);
  }
  return opts;
};

// ============================================================================
// 共通の「ノード」表示
//   value = 金額、pct = 全体（売上 or コスト）に占める比率（省略可）
// ============================================================================
function TreeNode({
  title, value, sub, tone = 'neutral', level = 'leaf',
}: {
  title: string;
  value: string;
  sub?: string;
  tone?: 'revenue' | 'cost' | 'neutral' | 'profit' | 'loss';
  /** 表示階層。root/branch/leaf でフォントの重みを変える */
  level?: 'root' | 'branch' | 'leaf';
}) {
  const bgMap: Record<string, string> = {
    revenue: 'bg-brand-red/10 border-brand-red/30',
    cost: 'bg-brand-orange/10 border-brand-orange/40',
    neutral: 'bg-neutral-panel border-neutral-line',
    profit: 'bg-success-bg border-success-border',
    loss: 'bg-warning-bg border-warning-border',
  };
  const titleSizeMap = { root: 'text-sm', branch: 'text-xs', leaf: 'text-xs' };
  const valueSizeMap = {
    root: 'text-lg font-bold',
    branch: 'text-base font-bold',
    leaf: 'text-sm font-semibold',
  };
  return (
    <div className={`rounded border ${bgMap[tone]} px-3 py-2 min-w-[9rem]`}>
      <div className={`${titleSizeMap[level]} text-neutral-sub`}>{title}</div>
      <div className={`${valueSizeMap[level]} tabular-nums`}>{value}</div>
      {sub && <div className="text-[11px] text-neutral-sub tabular-nums">{sub}</div>}
    </div>
  );
}

/** 「└ 」のようなインデントブランチ */
function Branch({ children }: { children: React.ReactNode }) {
  return (
    <div className="relative pl-4 border-l-2 border-neutral-line ml-2 py-1">
      <div className="absolute left-0 top-1/2 w-4 border-t-2 border-neutral-line" />
      {children}
    </div>
  );
}

// ============================================================================
// PlTreePage
// ============================================================================
export function PlTreePage() {
  const rootLabel = useRootLabel();
  const { storeId = '' } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const monthOptions = buildMonthOptions();
  const periodStart = searchParams.get('period_start') ?? monthOptions[0]!;

  const { data: stores = [] } = useQuery<Store[]>({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/stores'),
  });
  const storeName = stores.find((s) => s.store_id === storeId)?.store_name ?? storeId;

  const tree = useQuery<PlTreeResponse>({
    queryKey: ['pl-tree', storeId, periodStart],
    queryFn: () => apiFetch<PlTreeResponse>(
      `/dashboard/stores/${storeId}/pl-tree?period_start=${periodStart}`,
    ),
    enabled: Boolean(storeId && periodStart),
  });

  const onChangePeriod = (v: string) => {
    const next = new URLSearchParams(searchParams);
    next.set('period_start', v);
    setSearchParams(next, { replace: true });
  };

  const data = tree.data;
  const isProfit = (data?.operating_profit ?? 0) >= 0;

  // メニューを menu_code + effective_from で表示ソート
  const sortedCats = useMemo(() =>
    (data?.revenue.categories ?? []).map((cat) => ({
      ...cat,
      menus: [...cat.menus].sort((a, b) =>
        a.menu_code.localeCompare(b.menu_code) || a.effective_from.localeCompare(b.effective_from),
      ),
    })), [data?.revenue.categories]);

  return (
    <div className="space-y-6">
      {/* パンくず */}
      <nav aria-label="パンくずリスト" className="flex items-center gap-1 text-sm text-neutral-sub flex-wrap">
        <Link to="/dashboard" className="inline-flex items-center gap-1 hover:text-brand-red">
          <Home size={14} /> 経営ダッシュボード（{rootLabel}）
        </Link>
        <ChevronRight size={14} />
        <Link
          to={`/dashboard/stores/${storeId}?period_start=${periodStart}`}
          className="hover:text-brand-red"
        >
          {storeName}
        </Link>
        <ChevronRight size={14} />
        <span className="text-neutral-text font-medium">PL構造ツリー</span>
      </nav>

      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-h1">PL構造ツリー</h1>
          <p className="text-sm text-neutral-sub mt-1">
            {storeName} ／ {data?.period.label ?? ''}
          </p>
        </div>
        <select
          value={periodStart}
          onChange={(e) => onChangePeriod(e.target.value)}
          className="px-3 py-1.5 rounded border border-neutral-line text-sm focus-ring"
          aria-label="対象月"
        >
          {monthOptions.map((m) => (
            <option key={m} value={m}>{m.slice(0, 4)}年{Number(m.slice(5, 7))}月</option>
          ))}
        </select>
      </div>

      {tree.isLoading && <div className="text-neutral-sub">読み込み中…</div>}
      {tree.isError && (
        <div className="p-3 rounded bg-warning-bg text-warning-text border border-warning-border">
          データ取得に失敗しました：{(tree.error as Error).message}
        </div>
      )}

      {data && (
        <>
          {/* ─── レベル0: 営業利益ノード ───
              md未満: 縦積み、md以上: 横並び。子カラムはmd以上のみ min-w を課しoverflow-x-autoで対応 */}
          <div className="flex flex-col md:flex-row items-start gap-4 md:gap-8 md:overflow-x-auto pb-3">
            {/* Root: 営業利益 */}
            <div className="w-full md:w-auto md:shrink-0">
              <TreeNode
                title="営業利益"
                value={formatCurrency(data.operating_profit)}
                sub={data.revenue.total > 0
                  ? `利益率 ${formatPercent(data.operating_profit / data.revenue.total)}`
                  : undefined}
                tone={isProfit ? 'profit' : 'loss'}
                level="root"
              />
            </div>

            {/* Level 1 + 2 + 3 */}
            <div className="flex-1 w-full md:min-w-[600px] space-y-6">
              {/* === 売上枝 === */}
              <section>
                <TreeNode title="売上" value={formatCurrency(data.revenue.total)} tone="revenue" level="branch" />
                <div className="mt-2 ml-4 space-y-3">
                  {sortedCats.length === 0 && (
                    <Branch>
                      <div className="text-sm text-neutral-sub py-2">売上データがありません</div>
                    </Branch>
                  )}
                  {sortedCats.map((cat) => {
                    const isDelivery = cat.category_name === 'デリバリー売上';
                    return (
                    <Branch key={cat.menu_category_id ?? cat.category_name}>
                      <div className="flex items-start gap-4 flex-wrap">
                        <TreeNode
                          title={cat.category_name + (isDelivery ? '（メニュー非紐付）' : '')}
                          value={formatCurrency(cat.total)}
                          sub={data.revenue.total > 0
                            ? `売上構成 ${formatPercent(cat.total / data.revenue.total)}`
                            : undefined}
                        />
                        {cat.menus.length > 0 && (
                          <div className="flex-1 min-w-[280px] space-y-1">
                            {cat.menus.map((m) => (
                              <Branch key={m.menu_id}>
                                <div className="text-sm">
                                  <span className="font-medium">{m.menu_name}</span>
                                  <span className="ml-2 text-xs text-neutral-sub font-mono">
                                    #{m.menu_code}
                                  </span>
                                  {/* 月途中の版切替がある場合のみ期間を明示（月全体をカバーする版は非表示） */}
                                  {(m.effective_to && m.effective_to < data.period.end) || m.effective_from > data.period.start ? (
                                    <span className="ml-2 text-[11px] text-neutral-sub tabular-nums">
                                      ({m.effective_from}〜{m.effective_to ?? '無期限'})
                                    </span>
                                  ) : null}
                                </div>
                                <div className="text-xs text-neutral-sub tabular-nums">
                                  {formatCount(m.qty)} × {formatCurrency(m.unit_price)}
                                  <span className="mx-1">＝</span>
                                  <span className="font-medium text-neutral-text">{formatCurrency(m.amount)}</span>
                                </div>
                              </Branch>
                            ))}
                          </div>
                        )}
                      </div>
                    </Branch>
                    );
                  })}
                </div>
              </section>

              {/* === コスト枝 === */}
              <section>
                <TreeNode title="コスト" value={formatCurrency(data.cost.total)} tone="cost" level="branch" />
                <div className="mt-2 ml-4 space-y-3">
                  {/* 原価 */}
                  <Branch>
                    <div className="flex items-start gap-4 flex-wrap">
                      <TreeNode
                        title="原価"
                        value={formatCurrency(data.cost.cogs.total)}
                        sub={data.cost.cogs.ratio != null
                          ? `コスト内 ${formatPercent(data.cost.cogs.ratio)}`
                          : undefined}
                      />
                      <div className="flex-1 min-w-[280px] space-y-1">
                        {data.cost.cogs.categories.map((cc) => (
                          <Branch key={cc.cost_category_id ?? cc.category_name}>
                            <div className="flex items-start gap-4 flex-wrap">
                              <div className="text-sm min-w-[8rem]">
                                <div className="font-medium">{cc.category_name}</div>
                                <div className="text-xs text-neutral-sub tabular-nums">
                                  {formatCurrency(cc.total)}
                                  {cc.ratio != null && (
                                    <span className="ml-2">({formatPercent(cc.ratio)})</span>
                                  )}
                                </div>
                              </div>
                              {cc.items.length > 0 && (
                                <div className="flex-1 min-w-[220px] space-y-0.5">
                                  {cc.items.slice(0, 20).map((it, idx) => (
                                    <div key={`${cc.cost_category_id ?? '_'}-${idx}-${it.ingredient_name}`} className="text-xs">
                                      <span className="text-neutral-sub">└ </span>
                                      <span>{it.ingredient_name}</span>
                                      <span className="ml-2 tabular-nums text-neutral-sub">
                                        {formatCurrency(it.amount)}
                                        {it.ratio != null && (
                                          <span className="ml-1">({formatPercent(it.ratio)})</span>
                                        )}
                                      </span>
                                    </div>
                                  ))}
                                  {cc.items.length > 20 && (
                                    <div className="text-xs text-neutral-sub">…他 {cc.items.length - 20} 件</div>
                                  )}
                                </div>
                              )}
                            </div>
                          </Branch>
                        ))}
                        {data.cost.cogs.categories.length === 0 && (
                          <div className="text-sm text-neutral-sub py-1">原価データがありません</div>
                        )}
                      </div>
                    </div>
                  </Branch>

                  {/* 人件費 */}
                  <Branch>
                    <TreeNode
                      title="人件費"
                      value={formatCurrency(data.cost.labor.total)}
                      sub={data.cost.labor.ratio != null
                        ? `コスト内 ${formatPercent(data.cost.labor.ratio)}`
                        : undefined}
                    />
                    <p className="text-[11px] text-neutral-sub mt-1 ml-2">
                      ※ v0.1では内訳（法定福利費・交通費など）は保持していません
                    </p>
                  </Branch>

                  {/* その他諸経費 */}
                  <Branch>
                    <div className="flex items-start gap-4 flex-wrap">
                      <TreeNode
                        title="その他諸経費"
                        value={formatCurrency(data.cost.opex.total)}
                        sub={data.cost.opex.ratio != null
                          ? `コスト内 ${formatPercent(data.cost.opex.ratio)}`
                          : undefined}
                      />
                      <div className="flex-1 min-w-[280px] space-y-1">
                        {data.cost.opex.items.slice(0, 20).map((it, idx) => (
                          <Branch key={`opex-${idx}-${it.item_name}`}>
                            <div className="text-sm">
                              <span className="font-medium">{it.item_name}</span>
                              <span className="ml-2 text-xs text-neutral-sub tabular-nums">
                                {formatCurrency(it.amount)}
                                {it.ratio != null && (
                                  <span className="ml-1">({formatPercent(it.ratio)})</span>
                                )}
                              </span>
                            </div>
                          </Branch>
                        ))}
                        {data.cost.opex.items.length > 20 && (
                          <div className="text-xs text-neutral-sub pl-4">…他 {data.cost.opex.items.length - 20} 件</div>
                        )}
                        {data.cost.opex.items.length === 0 && (
                          <div className="text-sm text-neutral-sub py-1">諸経費データがありません</div>
                        )}
                      </div>
                    </div>
                  </Branch>
                </div>
              </section>
            </div>
          </div>

          {/* 凡例 */}
          <div className="mt-8 p-4 border border-neutral-line rounded-lg bg-neutral-panel/40 text-xs text-neutral-sub">
            <div className="font-medium text-neutral-text mb-2">凡例</div>
            <ul className="space-y-1">
              <li>• メニュー行の「{formatCount(1000)} × {formatCurrency(500)}」は「月間累計販売数 × 平均単価 = 月間累計売上」</li>
              <li>• 原価・諸経費のカッコ内 % は「月間コスト全体（原価 + 人件費 + 諸経費）に占める比率」</li>
              <li>• 月途中でメニューが改定された場合、同じメニューコードでも別行として表示されます（有効期間を併記）</li>
              <li>• 単価は該当バージョンのマスタ価格を採用。実売上は税抜集計値を使用しています</li>
            </ul>
          </div>
        </>
      )}
    </div>
  );
}
